import { useQuery } from '@apollo/client';
import { ITimeosUser, Permission, Rights } from '@dokucoach/common-types';
import AnalyticsIcon from 'assets/menu-icon/analytics-icon.svg';
import ConfigurationIcon from 'assets/menu-icon/configuration-icon.svg';
import DashboardIcon from 'assets/menu-icon/dashboard-icon.svg';
import DataIcon from 'assets/menu-icon/data-icon.svg';
import IndicatorsIcon from 'assets/menu-icon/indicators-icon.svg';
import InvoicingIcon from 'assets/menu-icon/invoicing-icon.svg';
import ShiftPlanningIcon from 'assets/menu-icon/shift-planning.svg';
import { useContextAuthManager } from 'components/AuthManager';
import { useContextLayout } from 'components/Layout/context';
import { useContextSnackbar } from 'components/Snackbar/context';
import MainMenu from 'constants/main-menu';
import routePaths from 'constants/route-paths';
import { Namespace } from 'locales';
import React, { useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useHistory } from 'react-router';
import { GET_TIMEOS_DATA } from 'services/timeos';
import styled from 'styled-components';
import HttpClient from 'utils/httpClient';

type PropMenuList = {
  id: MainMenu;
  title: string;
  icon: string;
  // used to redirect to a page of the dokucoach-application
  url?: string;
  // used to redirect to an external page
  href?: string;
  onClick?: () => Promise<unknown>;
  requiredPermissions: Permission[];
  className?: string;
};

export const Link = styled.a`
  &&:hover,
  &&.active {
    && span {
      svg path,
      svg rect {
        fill: ${(props) => props.theme.colors.malibu};
      }
    }
  }

  && span:not(.no-resize) {
    svg {
      width: 31px !important;
      height: 31px !important;
    }
  }
`;

const Menu = () => {
  const { t } = useTranslation();
  const { asideSelected, setAsideSelected } = useContextLayout();
  const { showApiError } = useContextSnackbar();

  const [timeosUser, setTimeosUser] = useState<ITimeosUser>();
  const [timeosCustomerId, setTimeosCustomerId] = useState<string>();

  const { data, error } = useQuery(GET_TIMEOS_DATA);

  useEffect(() => {
    if (error) {
      showApiError(error);
    }

    if (data?.timeosUser) {
      setTimeosUser(data.timeosUser);
    }

    if (data?.timeosCustomerId) {
      setTimeosCustomerId(data.timeosCustomerId);
    }
  }, [data, error]);

  const { user, hasOnePermission } = useContextAuthManager();

  const menuList: PropMenuList[] = useMemo(() => {
    const menuItems: PropMenuList[] = [];
    menuItems.push(
      ...[
        {
          id: MainMenu.DASHBOARD,
          title: t('dashboard'),
          icon: DashboardIcon,
          url: routePaths.DASHBOARD,
          requiredPermissions: [Rights.CARE.READ],
        },
        {
          id: MainMenu.BASE_DATA,
          title: t('data'),
          icon: DataIcon,
          url: routePaths.BASE_DATA,
          requiredPermissions: [
            Rights.RESIDENTS.READ,
            Rights.EMPLOYEES.READ,
            Rights.BASE_DATA.DOCTORS.READ,
            Rights.BASE_DATA.SUPERVISOR.READ,
          ],
        },
        {
          id: MainMenu.ANALYTICS,
          title: t('analytics'),
          icon: AnalyticsIcon,
          url: routePaths.LISTS,
          requiredPermissions: [Rights.LISTS.READ],
        },
      ],
    );

    if (user?.nursingHome.license.invoicingEnabledFrom) {
      menuItems.push({
        id: MainMenu.INVOICING,
        title: t('invoicing', { ns: Namespace.invoicing }),
        icon: InvoicingIcon,
        url: routePaths.INVOICING,
        requiredPermissions: [Rights.ADMINISTRATION_AND_INVOICING.INVOICE.READ],
      });
    }

    menuItems.push(
      ...[
        {
          id: MainMenu.INDICATORS,
          title: t('indicators'),
          icon: IndicatorsIcon,
          url: routePaths.INDICATORS,
          requiredPermissions: [Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ],
          className: 'no-resize',
        },
        {
          id: MainMenu.CONFIGURATION,
          title: t('configuration'),
          icon: ConfigurationIcon,
          url: routePaths.CONFIGURATION,
          requiredPermissions: [Rights.ADMINISTRATION_AND_INVOICING.ADMINISTRATE.READ],
        },
      ],
    );

    // Check if the User has a synchronized TimeosUser (= enabled)
    if (timeosUser?.isSynchronized && timeosCustomerId) {
      menuItems.push({
        id: MainMenu.ROSTER,
        title: t('roster'),
        icon: ShiftPlanningIcon,
        onClick: async () => {
          const res = await HttpClient.get(`user/timeosLoginUrl`);
          const redirectUrl = await res.text();
          window.open(redirectUrl);
        },
        requiredPermissions: [],
      });
    }
    return menuItems;
  }, [user, timeosUser, timeosCustomerId, t]);

  const history = useHistory();

  const handleTabChange = (id: string) => {
    const route = menuList.find((i) => i.id === id)?.url;
    if (!route || history.location.pathname === route) return;
    history.push(route);
  };

  return (
    <ul className="list-unstyled flex-column" role="tablist">
      {menuList.map((item, index) => {
        if (item.requiredPermissions.length && !hasOnePermission(item.requiredPermissions)) {
          return null;
        }
        return (
          <li
            key={index}
            className="nav-item mb-3"
            data-toggle="tooltip"
            data-placement="rigth"
            data-container="body"
            data-boundary="window"
            title={item.title}
          >
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id={item.id}>{item.title}</Tooltip>}
            >
              {item.onClick ? (
                <div
                  className={`nav-link btn btn-icon btn-clean btn-lg`}
                  onClick={async () => {
                    item.onClick && (await item.onClick());
                  }}
                >
                  <span className={'svg-icon svg-icon-lg ' + item.className}>
                    <SVG src={item.icon} />
                  </span>
                </div>
              ) : (
                <Link
                  href={item.href ?? '#'}
                  className={`nav-link btn btn-icon btn-clean btn-lg ${
                    asideSelected === item.id && 'active'
                  }`}
                  data-toggle="tab"
                  data-target={`#${item.id}`}
                  role="tab"
                  target="_blank"
                  onClick={async (event) => {
                    if (item.href) {
                      return true;
                    }

                    event.preventDefault();
                    handleTabChange(item.id);
                    setAsideSelected(item.id);
                    return false;
                  }}
                >
                  <span className={'svg-icon svg-icon-lg ' + item.className}>
                    <SVG src={item.icon} />
                  </span>
                </Link>
              )}
            </OverlayTrigger>
          </li>
        );
      })}
    </ul>
  );
};

export default Menu;
